import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RafWatchdogService implements OnDestroy {
  private animationFrameId: number | null = null;
  private lastFrameTime = performance.now();
  private frameTimeoutMs: number;
  private onFreezeDetected: Subject<number> = new Subject<number>();

  constructor() {
    this.frameTimeoutMs = 3000; // Default timeout, can be configurable
  }

  /**
   * Starts the RAF watchdog with a given timeout threshold.
   * @param timeoutMs Threshold in ms to detect tab freeze.
   */
  start(timeoutMs: number = 3000): void {
    this.frameTimeoutMs = timeoutMs;
    this.lastFrameTime = performance.now();

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }

    const loop = (time: number) => {
      const delta = time - this.lastFrameTime;

      if (delta > this.frameTimeoutMs) {
        this.onFreezeDetected.next(delta);
      }

      this.lastFrameTime = time;
      this.animationFrameId = requestAnimationFrame(loop);
    };

    this.animationFrameId = requestAnimationFrame(loop);
  }

  /**
   * Stops the RAF watchdog.
   */
  stop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Observable to subscribe to freeze detection events.
   * Emits the delta time when freeze is detected.
   */
  get freezeDetected$() {
    return this.onFreezeDetected.asObservable();
  }

  ngOnDestroy(): void {
    this.stop();
    this.onFreezeDetected.complete();
  }
}
